import { supabase, DEFAULT_ORG_ID, applyOrgFilter, getActiveOrgOverride } from '../supabaseClient';
import { getCurrentStaff } from './auth';
import { vaciosANulo, CAMPOS_TIPADOS_TORNEO } from '../camposVacios'

export const createTorneo = async (torneoData) => {
  try {
    const orgId = getActiveOrgOverride() || torneoData.org_id || (await getCurrentStaff())?.data?.org_id || DEFAULT_ORG_ID;
    // Un campo opcional en blanco es una ausencia, no una cadena vacia: Postgres rechaza
    // '' en una columna uuid o date. Sin esto no se podia crear un torneo sin llenar la
    // Liga y la Fecha de Fin, las dos marcadas OPCIONALES en la pantalla.
    const limpio = vaciosANulo(torneoData, CAMPOS_TIPADOS_TORNEO);
    const { data, error } = await supabase
      .from('torneos')
      .insert([{
        ...limpio,
        org_id: orgId,
        org_organizadora_id: torneoData.org_organizadora_id || orgId,
        formato: torneoData.formato || 'liga',
        activo: true
      }])
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error creating torneo:', error);
    return { data: null, error };
  }
};

export const updateTorneo = async (torneoId, torneoData) => {
  try {
    // Mismo motivo que en createTorneo: editar y vaciar la Liga o la Fecha de Fin
    // mandaba '' a una columna uuid/date y la edicion entera se rechazaba.
    const limpio = vaciosANulo(torneoData, CAMPOS_TIPADOS_TORNEO);
    const { data, error } = await supabase
      .from('torneos')
      .update({
        ...limpio,
        updated_at: new Date().toISOString()
      })
      .eq('id', torneoId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error updating torneo:', error);
    return { data: null, error };
  }
};

export const deleteTorneo = async (torneoId) => {
  try {
    const { data, error } = await supabase
      .from('torneos')
      .update({
        activo: false
      })
      .eq('id', torneoId)
      .select('id')
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error deleting torneo:', error);
    return { data: null, error };
  }
};

// =====================================================
// Que grupos juegan que torneo
//
// Estas dos leian `torneos_grupos`, y esa tabla esta MUERTA: nadie la escribe desde
// que Fase 6 movio todo a `torneos_inscripciones` -- linkGroupToTorneo existia en este
// mismo archivo pero ningun componente la llamo nunca, en toda la historia del repo.
// En QA parecia sana solo porque el seed de demo llena las dos tablas a mano; en
// produccion tiene 2 filas de una copa demo finalizada.
//
// O sea que un club que inscribia un grupo NO lo veia despues en "Nuevo juego", ni en
// el rol, ni en estadisticas: el selector salia vacio y no habia error que mirar.
//
// Se conserva la forma del resultado a proposito, para que las cinco pantallas que las
// consumen no se enteren del cambio.
// =====================================================

export const getGruposAsignadosByTorneo = async (torneoId) => {
  try {
    const { data, error } = await supabase
      .from('torneos_inscripciones')
      .select(`
        id,
        grupo_id,
        activo,
        grupos:grupo_id (
          id,
          nombre_del_grupo,
          categoria_base,
          rama
        )
      `)
      .eq('torneo_id', torneoId)
      .eq('activo', true)
      // Una solicitud sin resolver todavia no juega: solo las aceptadas arman el rol.
      .eq('estatus', 'aceptada');

    if (error) throw error;

    const formatted = (data || []).map((insc) => ({
      id: insc.grupo_id,
      inscripcion_id: insc.id,
      nombre_del_grupo: insc.grupos?.nombre_del_grupo || 'Grupo sin nombre',
      categoria_base: insc.grupos?.categoria_base || null,
      rama: insc.grupos?.rama || null
    }));

    return { data: formatted, error: null };
  } catch (error) {
    console.error('Error getting torneo groups:', error);
    return { data: [], error };
  }
};

export const getTorneosByGrupoIds = async (grupoIds = []) => {
  if (!Array.isArray(grupoIds) || grupoIds.length === 0) {
    return { data: [], error: null };
  }
  try {
    const { data, error } = await supabase
      .from('torneos_inscripciones')
      .select(`
        id,
        torneo_id,
        grupo_id,
        estatus,
        activo,
        grupos:grupo_id (
          id,
          nombre_del_grupo,
          rama,
          deporte
        ),
        torneos:torneo_id!inner (
          id,
          nombre,
          descripcion,
          fecha_inicio,
          fecha_fin,
          estatus,
          etapa,
          tipo_torneo,
          activo,
          org_organizadora_id,
          liga:ligas(nombre)
        )
      `)
      .in('grupo_id', grupoIds)
      .eq('activo', true)
      .eq('estatus', 'aceptada')
      .eq('torneos.activo', true)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error getting torneos by grupos:', error);
    return { data: null, error };
  }
};

// =====================================================
// Fase 6: lecturas con modelo nuevo (inscripciones + record + organizador)
// =====================================================

// Los torneos del club, LIGEROS: lo justo para un desplegable y para decidir el mundo.
// getAllTorneosWithInscripciones trae ademas todas las inscripciones con sus grupos, y
// la pantalla de Juegos la esperaba solo para llenar el selector de "Crear juego".
export const getTorneosDelClub = async () => {
  try {
    let query = supabase
      .from('torneos')
      .select('id, nombre, sede, fecha_inicio, fecha_fin, estatus, etapa, activo, org_organizadora_id, temporada_id, logo_url, temporadas:temporada_id (deporte_id)')
      .eq('activo', true)
      .order('fecha_inicio', { ascending: false, nullsFirst: false });
    query = applyOrgFilter(query);
    const { data, error } = await query;
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('[getTorneosDelClub]', error);
    return { data: [], error };
  }
};

export const getAllTorneosWithInscripciones = async () => {
  try {
    let query = supabase
      .from('torneos')
      .select(`
        *,
        ligas (id, nombre, organizacion, logo_url),
        organizadora:org_organizadora_id (id, name, slug, logo_url, type),
        torneos_inscripciones (
          id,
          grupo_id,
          club_org_id,
          categoria,
          categoria_id,
          estatus,
          activo,
          fase,
          roster_estado,
          partidos_jugados,
          ganados,
          empatados,
          perdidos,
          puntos_favor,
          puntos_contra,
          grupos:grupo_id (
            id, nombre_del_grupo, rama, categoria_base, estatus, tipo_de_grupo
          )
        )
      `)
      .order('fecha_inicio', { ascending: true })
      .order('nombre', { ascending: true });

    query = applyOrgFilter(query);

    const { data, error } = await query;
    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('[getAllTorneosWithInscripciones]', error);
    return { data: [], error };
  }
};

export const getTorneoByIdWithInscripciones = async (torneoId) => {
  try {
    const { data, error } = await supabase
      .from('torneos')
      .select(`
        *,
        ligas (id, nombre, organizacion, logo_url),
        temporadas:temporada_id (id, nombre, deporte_id),
        organizadora:org_organizadora_id (id, name, slug, logo_url, type),
        torneos_inscripciones (
          id,
          grupo_id,
          club_org_id,
          categoria,
          categoria_id,
          estatus,
          activo,
          fase,
          roster_estado,
          partidos_jugados,
          ganados,
          empatados,
          perdidos,
          puntos_favor,
          puntos_contra,
          grupos:grupo_id (
            id, nombre_del_grupo, rama, categoria_base, estatus, tipo_de_grupo
          ),
          categorias:categoria_id (id, nombre)
        )
      `)
      .eq('id', torneoId)
      .eq('activo', true)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[getTorneoByIdWithInscripciones]', error);
    return { data: null, error };
  }
};
